import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material';
import { ChangeEvent } from 'react';

interface ApplicationSelectionProps {
  availableApplications: string[];
  handleCheckboxChange: (
    elem: string,
    setList: React.Dispatch<React.SetStateAction<string[]>>,
    list: string[],
    paramKey: string
  ) => (event: ChangeEvent<HTMLInputElement>) => void;
  applicationsList: string[];
  setApplicationsList: React.Dispatch<React.SetStateAction<string[]>>;
  itemGroup: string;
  applyFiltersForApplications: (applications: string[]) => void;
}

const ApplicationSelection = ({
                                availableApplications,
                                applicationsList,
                                setApplicationsList,
                                itemGroup,
                                applyFiltersForApplications
                              }: ApplicationSelectionProps) => {
  // Create a dedicated handler for application checkbox changes
  const handleApplicationChange = (applicationName: string) => (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;

    // Create a new list of applications based on the checkbox state and special combinations
    let updatedList: string[] = [...applicationsList];

    if (isChecked) {
      // Handle special combinations for item group 5
      if (itemGroup === '005') {
        if (applicationName === 'Curtaining') {
          // When Curtaining is selected, add both Curtaining and Dual Purpose
          updatedList = updatedList.filter(app => app !== 'Upholstery'); // Remove Upholstery if present
          if (!updatedList.includes('Curtaining')) updatedList.push('Curtaining');
          if (!updatedList.includes('Dual Purpose')) updatedList.push('Dual Purpose');
        }
        else if (applicationName === 'Upholstery') {
          // When Upholstery is selected, add both Upholstery and Dual Purpose
          updatedList = updatedList.filter(app => app !== 'Curtaining'); // Remove Curtaining if present
          if (!updatedList.includes('Upholstery')) updatedList.push('Upholstery');
          if (!updatedList.includes('Dual Purpose')) updatedList.push('Dual Purpose');
        }
        else if (applicationName === 'Dual Purpose') {
          // When Dual Purpose is selected alone, only add Dual Purpose
          updatedList = ['Dual Purpose'];
        }
      } else {
        // For other item groups, just add the selected application
        if (!updatedList.includes(applicationName)) {
          updatedList.push(applicationName);
        }
      }
    } else {
      // Handle unchecking logic for item group 5
      if (itemGroup === '005') {
        if (applicationName === 'Curtaining') {
          // If unchecking Curtaining, remove both Curtaining and potentially Dual Purpose
          updatedList = updatedList.filter(app => app !== 'Curtaining');
          // Only remove Dual Purpose if Upholstery isn't selected
          if (!updatedList.includes('Upholstery')) {
            updatedList = updatedList.filter(app => app !== 'Dual Purpose');
          }
        }
        else if (applicationName === 'Upholstery') {
          // If unchecking Upholstery, remove both Upholstery and potentially Dual Purpose
          updatedList = updatedList.filter(app => app !== 'Upholstery');
          // Only remove Dual Purpose if Curtaining isn't selected
          if (!updatedList.includes('Curtaining')) {
            updatedList = updatedList.filter(app => app !== 'Dual Purpose');
          }
        }
        else if (applicationName === 'Dual Purpose') {
          // When unchecking Dual Purpose, clear all selections
          updatedList = [];
        }
      } else {
        // For other item groups, just remove the application
        updatedList = updatedList.filter(app => app !== applicationName);
      }
    }

    // Update the application list state
    setApplicationsList(updatedList);

    // Immediately apply filters with the updated list
    applyFiltersForApplications(updatedList);
  };

  return (
    <Box>
      <Divider sx={{ backgroundColor: '#000000', mb: '1rem' }} />
      <Typography fontWeight='bold' fontSize='20px' mb={2}>
        Applications
      </Typography>

      <Box sx={{ overflowY: 'auto', maxHeight: '20.5rem' }}>
        <FormGroup>
          {availableApplications
            ?.sort()
            .map((elem: string) => (
              <FormControlLabel
                key={elem}
                control={
                  <Checkbox
                    checked={applicationsList.includes(elem)}
                    onChange={handleApplicationChange(elem)}
                  />
                }
                label={elem}
              />
            ))}
        </FormGroup>
      </Box>
    </Box>
  );
};

export default ApplicationSelection;